import { functions } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'
import { showLoading, hideLoading } from '@/core/ui'
import { initializeAccountContext } from '@/account/init'
import { currentUser } from '@/core/state'

/**
 * Shows a modal for creating a new brand.
 * @param onBrandCreated A callback function to run after a brand is successfully created.
 */
export function showCreateBrandModal(
  onBrandCreated: () => void,
): void {
  // Create modal backdrop
  const backdrop = document.createElement('div')
  backdrop.id = 'create-brand-modal-backdrop'
  backdrop.className =
    'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  backdrop.style.backdropFilter = 'blur(4px)'

  // Create modal container
  const modal = document.createElement('div')
  modal.className =
    'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden'

  // Create modal content
  modal.innerHTML = `
    <div class="p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-bold text-gray-900">
          Create New Brand
        </h2>
        <button
          id="close-create-brand-modal"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <form id="create-brand-form-modal" class="space-y-4">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            Brand Name
          </label>
          <input
            type="text"
            id="create-brand-name-input"
            placeholder="e.g., Kopi Suka-Suka"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
            minlength="2"
            maxlength="100"
          />
        </div>

        <div id="create-brand-error" class="hidden p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600"></p>
        </div>
        
        <div id="create-brand-success" class="hidden p-3 bg-green-50 border border-green-200 rounded-lg">
          <p class="text-sm text-green-600">Brand created! Refreshing...</p>
        </div>

        <div class="flex gap-3 pt-2">
          <button
            type="submit"
            id="create-brand-submit-btn"
            class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Brand
          </button>
          <button
            type="button"
            id="cancel-create-brand-btn"
            class="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const form = modal.querySelector('#create-brand-form-modal') as HTMLFormElement
  const closeBtn = modal.querySelector('#close-create-brand-modal')
  const cancelBtn = modal.querySelector('#cancel-create-brand-btn')
  const submitBtn = modal.querySelector('#create-brand-submit-btn') as HTMLButtonElement
  const brandNameInput = modal.querySelector('#create-brand-name-input') as HTMLInputElement
  const errorDiv = modal.querySelector('#create-brand-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  
  // --- [THIS IS THE FIX] ---
  // Get the new success message div
  const successDiv = modal.querySelector('#create-brand-success') as HTMLElement
  // --- [END FIX] ---

  const closeModal = () => {
    backdrop.remove()
  }

  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      closeModal()
    }
  })

  form?.addEventListener('submit', async (e) => {
    e.preventDefault()
    const brandName = brandNameInput.value.trim()
    if (!brandName) {
      errorDiv.classList.remove('hidden')
      if (errorText) errorText.textContent = 'Please enter a brand name.'
      return
    }

    errorDiv.classList.add('hidden')
    submitBtn.disabled = true
    submitBtn.textContent = 'Creating...'

    try {
      const createBrand = httpsCallable(functions, 'createBrand')
      const result = await createBrand({ brandName })

      const data = result.data as {
        success: boolean; message: string; brandId: string; brandName: string
      }
      if (!data.success) {
        throw new Error(data.message || 'Failed to create brand.')
      }

      // --- [THIS IS THE FIX] ---
      // Show success message and reload the page
      successDiv.classList.remove('hidden')
      submitBtn.textContent = 'Success!'
      
      // We no longer need to call initializeAccountContext or onBrandCreated
      // The reload will handle all state refreshing.
      
      setTimeout(() => {
        window.location.reload()
      }, 2000)
      // --- [END FIX] ---

    } catch (error: any) {
      console.error('[CreateBrandModal] Error:', error)
      if (errorText) errorText.textContent = error.message || 'An unknown error occurred.'
      errorDiv.classList.remove('hidden')
      submitBtn.disabled = false
      submitBtn.textContent = 'Create Brand'
    }
  })
}